import { useMutation } from '@apollo/client';
import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { FeatureFlag, GeneralFeedbackReason, NOVA_FEEDBACK_DETAIL_MAX_LENGTH } from '@newfront-insurance/ai-api';
import { Button, cn, Popover, PopoverContent, PopoverTrigger } from '@newfront-insurance/core-ui/v2';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import { graphql, type VariablesOf } from '@newfront-insurance/graphql-frontend';
import { useNotifications } from '@newfront-insurance/next-notifications';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useId, useRef, useState } from 'react';

import { NOVA_ANALYTICS_EVENTS, trackNovaEvent } from '../../lib/analytics';
import {
  FeedbackReasonButton,
  FeedbackReasonGroup,
  FEEDBACK_ACTION_GRADIENT,
  FEEDBACK_POPOVER_POSITION,
  FEEDBACK_POPOVER_SHELL_CLASS_NAME,
} from '../feedback-controls';

const SUBMIT_NOVA_FEEDBACK = graphql(`
  mutation SubmitNovaFeedback($input: GeneralFeedbackInput!) {
    submitNovaFeedback(input: $input) {
      uuid
    }
  }
`);

type GeneralFeedbackInput = VariablesOf<typeof SUBMIT_NOVA_FEEDBACK>['input'];
type GraphQLGeneralFeedbackReason = GeneralFeedbackInput['reason'];

interface GeneralFeedbackButtonProps {
  className?: string;
}

export function GeneralFeedbackButton({ className }: GeneralFeedbackButtonProps) {
  const feedbackEnabled = useFeatureFlag(FeatureFlag.NOVA_CONTEXTUAL_FEEDBACK_REPORTING, false);
  const detailId = useId();
  const headingId = useId();
  const firstReasonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState<GraphQLGeneralFeedbackReason>();
  const [detail, setDetail] = useState('');
  const [error, setError] = useState<string>();
  const [submitFeedback, { loading }] = useMutation(SUBMIT_NOVA_FEEDBACK, {
    context: {
      notifications: { preventErrorNotification: true },
      retry: { maxAttempts: 3 },
    },
  });
  const notifications = useNotifications();
  const analytics = useAnalytics();
  if (!feedbackEnabled) return null;

  const feedbackOptions = [
    { value: GeneralFeedbackReason.FEATURE_REQUEST, label: 'Feature request' },
    { value: GeneralFeedbackReason.SOMETHING_BROKE, label: 'Something broke' },
  ] as const satisfies ReadonlyArray<{ label: string; value: GraphQLGeneralFeedbackReason }>;
  const activeReason = selectedReason ?? GeneralFeedbackReason.FEATURE_REQUEST;
  const isProblemReport = activeReason === GeneralFeedbackReason.SOMETHING_BROKE;
  const popoverHeightClassName = isProblemReport ? 'min-h-[22rem]' : 'min-h-[19.5rem]';

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    setError(undefined);
    if (nextOpen) {
      setSelectedReason(GeneralFeedbackReason.FEATURE_REQUEST);
      setDetail('');
      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.GENERAL_FEEDBACK_OPENED);
    }
  };

  const handleSubmit = async () => {
    if (loading) return;
    const trimmedDetail = detail.trim();
    if (!trimmedDetail) {
      setError('Please enter feedback before submitting.');
      return;
    }

    setError(undefined);
    try {
      const input = {
        reason: activeReason,
        detail: trimmedDetail,
      } satisfies GeneralFeedbackInput;
      const result = await submitFeedback({
        variables: {
          input,
        },
      });
      if (result.errors?.length || !result.data?.submitNovaFeedback.uuid) {
        throw new Error(result.errors?.[0]?.message ?? 'Feedback save returned no identifier');
      }
      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.GENERAL_FEEDBACK_SUBMITTED, {
        hasDetail: true,
        reason: activeReason,
      });
      notifications.add({ type: 'success', duration: 3000, title: 'Thank you for your feedback!' });
      setOpen(false);
    } catch {
      setError('Feedback could not be sent. Please try again.');
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="secondary"
          className={cn(
            'border-steel-200 text-steel-500 hover:border-steel-300 h-8 rounded-[30px] bg-white px-2.5 py-2',
            'text-xs leading-4 font-normal transition-colors',
            'focus-visible:outline-wtw-infinity-500 focus-visible:outline-2 focus-visible:outline-offset-2',
            className,
          )}
        >
          Feedback
        </Button>
      </PopoverTrigger>
      <PopoverContent
        role="dialog"
        aria-labelledby={headingId}
        aria-modal="false"
        {...FEEDBACK_POPOVER_POSITION}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          firstReasonRef.current?.focus();
        }}
        className={cn(FEEDBACK_POPOVER_SHELL_CLASS_NAME, 'w-[min(28.75rem,calc(100vw-2rem))]', popoverHeightClassName)}
      >
        <div className="p-4">
          <h3 id={headingId} className="text-sm leading-5 font-semibold text-black">
            What’s your feedback?
          </h3>
          <FeedbackReasonGroup className="mt-4 flex flex-wrap gap-[3px]" aria-label="Feedback type">
            {feedbackOptions.map((option, index) => {
              const selected = activeReason === option.value;

              return (
                <FeedbackReasonButton
                  key={option.value}
                  ref={index === 0 ? firstReasonRef : undefined}
                  selected={selected}
                  className={cn('text-sm leading-4', selected ? 'px-4' : 'w-32 px-2.5')}
                  onClick={() => {
                    setSelectedReason(option.value);
                    setError(undefined);
                  }}
                >
                  {option.label}
                </FeedbackReasonButton>
              );
            })}
          </FeedbackReasonGroup>
        </div>

        <div className="border-steel-200 border-t p-4">
          <label htmlFor={detailId} className="block text-sm leading-5 font-semibold text-black">
            {isProblemReport ? 'What happened?' : 'Tell us more'}
          </label>
          <textarea
            id={detailId}
            value={detail}
            rows={4}
            maxLength={NOVA_FEEDBACK_DETAIL_MAX_LENGTH}
            required
            onChange={(event) => {
              setDetail(event.target.value);
              setError(undefined);
            }}
            placeholder={isProblemReport ? 'Share your experience' : 'It would be helpful if Nova could…'}
            className={cn(
              'border-steel-200 text-steel-500 placeholder:text-steel-300 mt-2.5 block h-[6.25rem] w-full resize-none',
              'focus:border-steel-400 rounded-[0.75rem] border p-3 text-sm leading-5 outline-none',
            )}
          />
          {error && (
            <p className="text-fire-600 mt-2 text-xs" role="alert">
              {error}
            </p>
          )}
          <div className="mt-2.5 flex justify-end">
            <button
              type="button"
              disabled={loading || !detail.trim()}
              onClick={() => void handleSubmit()}
              className={cn(
                'inline-flex h-9 items-center justify-center gap-2.5 rounded-full px-4',
                'text-sm leading-5 font-semibold text-[#350E5E] transition-opacity hover:opacity-90',
                'disabled:cursor-not-allowed disabled:opacity-50',
                FEEDBACK_ACTION_GRADIENT,
              )}
            >
              {loading ? (
                <Loader2 className="size-[1.125rem] animate-spin" aria-hidden="true" />
              ) : (
                <ArrowRight className="size-[1.125rem]" aria-hidden="true" />
              )}
              Submit
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
